import { playerToSlug } from "@/lib/playerSlug";
import { useState, useEffect, useCallback } from "react";
import { Football, GoogleLogo, TrendUp, TrendDown, Minus, MagnifyingGlass, ArrowRight, Lightning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import { useIsAppNavVisible } from "@/hooks/useIsAppNavVisible";
import PublicNav from "@/components/PublicNav";

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>{pos}</span>;
}

function TierBadge({ value }) {
  if (value >= 6000) return <span className="text-[8px] font-bold uppercase px-2 py-0.5 bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 rounded-sm">TIER 1</span>;
  if (value >= 4000) return <span className="text-[8px] font-bold uppercase px-2 py-0.5 bg-[#4ade80]/10 text-[#4ade80] border border-[#4ade80]/30 rounded-sm">TIER 2</span>;
  if (value >= 2500) return <span className="text-[8px] font-bold uppercase px-2 py-0.5 bg-[#fbbf24]/10 text-[#fbbf24] border border-[#fbbf24]/30 rounded-sm">TIER 3</span>;
  if (value >= 1500) return <span className="text-[8px] font-bold uppercase px-2 py-0.5 bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/30 rounded-sm">TIER 4</span>;
  return <span className="text-[8px] font-bold uppercase px-2 py-0.5 bg-[#262626] text-[#666] rounded-sm">TIER 5</span>;
}

export default function RookieRankings() {
  const [rookies, setRookies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [format, setFormat] = useState("sf");
  const [posFilter, setPosFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const hideNav = useIsAppNavVisible();

  const fetchRookies = useCallback(async () => {
    setLoading(true);
    try {
      const numQbs = format === "sf" ? 2 : 1;
      const res = await axios.get(`${API}/fantasycalc/values?numQbs=${numQbs}&numTeams=12&ppr=1`);
      const data = res.data.filter(p => p.player?.maybeYoe === 0 && !p.player.name?.includes("Pick") && !p.player.name?.startsWith("20"));
      data.sort((a, b) => b.value - a.value);
      setRookies(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [format]);

  useEffect(() => { fetchRookies(); }, [fetchRookies]);

  const filtered = rookies.filter(p => {
    const name = p.player?.name?.toLowerCase() || "";
    const pos = p.player?.position || "";
    if (posFilter !== "ALL" && pos !== posFilter) return false;
    if (search && !name.includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by tiers
  const tiers = [
    { name: "TIER 1 — ELITE", min: 6000, color: "#ccff00", desc: "Franchise-changing prospects. Draft at all costs." },
    { name: "TIER 2 — HIGH VALUE", min: 4000, color: "#4ade80", desc: "Strong starters with top-12 potential at their position." },
    { name: "TIER 3 — SOLID", min: 2500, color: "#fbbf24", desc: "Startable contributors with upside. Good mid-round value." },
    { name: "TIER 4 — DEPTH", min: 1500, color: "#3b82f6", desc: "Roster depth and developmental stashes." },
    { name: "TIER 5 — DART THROWS", min: 0, color: "#666", desc: "Late-round fliers and long-term stashes." },
  ];

  const handleGoogleLogin = () => { const r = window.location.origin + '/'; window.location.href = '/api/auth/google/login'; };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Rookie Rankings 2026, Tiered — Dynasty Trade Maker" description="Tiered 2026 dynasty rookie rankings, updated for post-draft landing spots. See who's worth a first-round rookie pick and who's a late-round dart throw." path="/rookie-rankings" />
<PublicNav />

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00]">2026 ROOKIE CLASS</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white uppercase mt-1 mb-2" data-testid="rookie-rankings-title">DYNASTY ROOKIE <span className="text-[#ccff00]">RANKINGS</span></h1>
          <p className="text-sm text-[#a3a3a3]">Tiered rankings of the 2026 rookie class based on dynasty trade values. Updated regularly.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex border border-[#262626] rounded-sm overflow-hidden">
            <button onClick={() => setFormat("sf")} className={`px-4 py-2 text-xs font-bold uppercase ${format === "sf" ? 'bg-[#ccff00] text-black' : 'bg-[#171717] text-[#a3a3a3]'}`}>SUPERFLEX</button>
            <button onClick={() => setFormat("1qb")} className={`px-4 py-2 text-xs font-bold uppercase ${format === "1qb" ? 'bg-[#ccff00] text-black' : 'bg-[#171717] text-[#a3a3a3]'}`}>1QB</button>
          </div>
          <div className="flex gap-1">
            {["ALL", "QB", "RB", "WR", "TE"].map(pos => (
              <button key={pos} onClick={() => setPosFilter(pos)} className={`px-3 py-2 text-xs font-bold uppercase border rounded-sm ${posFilter === pos ? 'border-[#ccff00] text-[#ccff00] bg-[#ccff00]/10' : 'border-[#262626] text-[#666]'}`}>{pos}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input placeholder="Search rookie..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 pl-8 bg-[#171717] border-[#262626] text-white text-sm placeholder:text-[#444]" />
          </div>
        </div>

        {/* Tiered Rankings */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-40 skeleton rounded-sm" />)}</div>
        ) : (
          <div className="space-y-6" data-testid="rookie-tiers">
            {tiers.map(tier => {
              const tierPlayers = filtered.filter(p => p.value >= tier.min && (tier.min === 0 || p.value < (tiers[tiers.indexOf(tier) - 1]?.min || 99999)));
              if (tierPlayers.length === 0) return null;
              return (
                <div key={tier.name} className="border bg-[#171717]" style={{ borderColor: `${tier.color}30` }}>
                  <div className="px-5 py-3 border-b" style={{ borderColor: `${tier.color}30`, backgroundColor: `${tier.color}08` }}>
                    <div className="flex items-center justify-between">
                      <h2 className="font-heading text-lg uppercase" style={{ color: tier.color }}>{tier.name}</h2>
                      <span className="text-[10px] text-[#666]">{tierPlayers.length} players</span>
                    </div>
                    <p className="text-[10px] text-[#a3a3a3]">{tier.desc}</p>
                  </div>
                  {tierPlayers.map((pv, idx) => (
                    <Link key={pv.player.id} to={`/players/${playerToSlug(pv.player.name)}`} className="flex items-center justify-between px-5 py-3 border-b border-[#1a1a1a] hover:bg-[#262626] transition-colors no-underline">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-[#666] w-6">{filtered.indexOf(pv) + 1}</span>
                        <PosBadge pos={pv.player.position} />
                        <div>
                          <span className="text-sm font-semibold text-white">{pv.player.name}</span>
                          <span className="text-[10px] text-[#666] ml-2">{pv.player.maybeTeam || "TBD"} {pv.player.maybeAge ? `/ ${pv.player.maybeAge}y` : ''}</span>
                        </div>
                      </div>
                      <span className={`font-mono font-bold text-sm ${pv.value >= 6000 ? 'text-[#ccff00]' : pv.value >= 4000 ? 'text-[#4ade80]' : pv.value >= 2500 ? 'text-[#fbbf24]' : 'text-[#a3a3a3]'}`}>{pv.value.toLocaleString()}</span>
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Draft CTA */}
        <div className="mt-8 border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-xl text-white uppercase mb-2">PREP FOR YOUR ROOKIE DRAFT</h3>
          <p className="text-sm text-[#a3a3a3] mb-4">Import your Sleeper league and use the Draft War Room for pick-by-pick AI advice, mock draft simulation, and trade-this-pick analysis.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => window.location.href = "/"} className="h-10 px-6 btn-primary rounded-sm text-xs"><Lightning size={14} weight="bold" className="mr-1" />IMPORT YOUR LEAGUE</Button>
            <Link to="/features/draft-room" className="text-xs text-[#ccff00] no-underline hover:underline flex items-center gap-1">Draft War Room <ArrowRight size={12} /></Link>
          </div>
        </div>

        {/* SEO Content */}
        <div className="mt-10 border-t border-[#262626] pt-10 max-w-3xl mx-auto space-y-6">
          <div>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">DYNASTY ROOKIE RANKINGS 2026</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed">Rookie value moves faster than any other tier in dynasty — a landing spot, a depth chart change, or one good camp report can shift a player two tiers overnight. These rankings are tiered rather than just numbered, because at this stage the gap between tiers matters more than the gap between two names inside one.</p>
          </div>
          <div>
            <p className="text-sm text-[#a3a3a3] leading-relaxed"><strong className="text-white">How to use tiers:</strong> If you're picking inside a tier, take the player whose situation you believe in most — the value gap between them is small enough that fit matters more than rank. If you're choosing between tiers, take the higher tier almost every time.</p>
          </div>
          <div>
            <h3 className="font-heading text-lg text-white uppercase mb-2">PREP FOR YOUR ROOKIE DRAFT</h3>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-4">Rankings tell you value. They don't tell you who'll actually be there when your pick comes up. Import your Sleeper league and use the Draft War Room for a real draft board, pick-by-pick AI advice, and a full mock draft simulator before your rookie draft.</p>
            <div className="flex flex-wrap gap-3">
              <a href="/" className="h-9 px-5 btn-primary rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold">IMPORT YOUR LEAGUE</a>
              <Link to="/features/draft-room" className="h-9 px-5 border border-[#ccff00] text-[#ccff00] rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#ccff00]/10 transition-colors">OPEN DRAFT WAR ROOM</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
